import React, { useState } from 'react';
import { Box, IconButton, Typography, Grid } from '@mui/material';
import { IMessage } from '../../redux/messages/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useDispatch } from 'react-redux';
import { setmedia } from '../../redux/project/actions';
import { MessageActions } from './MessageActions';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IProject } from '../../types/global/helper';
import CustomLazyLoadImage from '../../components/Image/CustomLazyLoadImage';

interface VideoMessageComponentProps {
    message: IMessage;
    isCurrentUser: boolean;
    onReactionClick?: (emoji: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    onEditMessage?: (messageId: string, newContent: string) => void;
    formatMessageTime?: (timestamp?: string) => string;
}

export const VideoMessageComponent: React.FC<VideoMessageComponentProps> = ({
    message,
    isCurrentUser,
    onReactionClick = () => {},
    onDeleteMessage,
    onEditMessage,
    formatMessageTime = () => '',
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [playingIndex, setPlayingIndex] = useState<number | null>(null);

    // Ensure media is an array and has content
    const videos = Array.isArray(message.media) ? message.media : [];

    // Calculate grid configuration based on number of videos
    const getGridConfig = () => {
        const count = videos.length;
        if (count <= 1) return { cols: 1, height: { xs: '180px', sm: '220px' } };
        if (count <= 2) return { cols: 2, height: { xs: '140px', sm: '180px' } };
        if (count <= 4) return { cols: 2, height: { xs: '120px', sm: '160px' } };
        return { cols: 3, height: { xs: '100px', sm: '140px' } };
    };

    const gridConfig = getGridConfig();

    // Get container width based on number of videos
    const getContainerWidth = () => {
        const count = videos.length;
        if (count <= 1) return { xs: '70vw', sm: '280px' };
        if (count <= 2) return { xs: '80vw', sm: '320px' };
        if (count <= 4) return { xs: '90vw', sm: '360px' };
        return { xs: '95vw', sm: '420px' };
    };

    const handlePlay = (video: IProject, index: number) => {
        if (playingIndex === index) {
            // If already playing this video, stop it
            setPlayingIndex(null);
        } else {
            // Otherwise play this video
            setPlayingIndex(index);
            dispatch(
                setmedia({
                    media: video,
                    mediaType: 'act',
                }),
            );
        }
    };

    // If no videos, don't render anything
    if (!videos.length) {
        return null;
    }

    return (
        <Box
            sx={{
                width: getContainerWidth(),
                maxWidth: '95%',
                borderRadius: '16px',
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: theme[themeStyle].deep_grey,
                border: `1px solid ${theme[themeStyle].grey}30`,
                display: 'flex',
                alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
                justifySelf: isCurrentUser ? 'flex-end' : 'flex-start',
                flexDirection: 'column',
            }}>
            {/* Video Thumbnails Grid */}
            <Grid container spacing={1} sx={{ p: 1 }}>
                {videos.map((video, index) => (
                    <Grid item xs={12 / gridConfig.cols} key={video._id}>
                        <Box
                            sx={{
                                height: gridConfig.height,
                                position: 'relative',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                '&:hover': {
                                    '& .play-overlay': {
                                        opacity: 1,
                                    },
                                },
                            }}
                            onClick={() => handlePlay(video, index)}>
                            {/* Video Thumbnail */}
                            <CustomLazyLoadImage
                                src={video.thumbnail || video.feature_image}
                                alt={video.title || 'Video thumbnail'}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />

                            {/* Play/Pause Button Overlay */}
                            <Box
                                className="play-overlay"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(0,0,0,0.4)',
                                    opacity: playingIndex === index ? 1 : 0.7,
                                    transition: 'opacity 0.3s',
                                }}>
                                <IconButton
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight + '90',
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].highlight,
                                        },
                                    }}>
                                    {playingIndex === index ? (
                                        <PauseIcon
                                            sx={{
                                                color: theme[themeStyle].true_white,
                                                fontSize: videos.length > 1 ? 24 : 40,
                                            }}
                                        />
                                    ) : (
                                        <PlayArrowIcon
                                            sx={{
                                                color: theme[themeStyle].true_white,
                                                fontSize: videos.length > 1 ? 24 : 40,
                                            }}
                                        />
                                    )}
                                </IconButton>
                            </Box>

                            {/* Video Title Overlay */}
                            {videos.length > 1 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        background: 'rgba(0,0,0,0.7)',
                                        p: 0.5,
                                    }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'white',
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block',
                                            fontSize: '10px',
                                        }}>
                                        {video.title || 'Video'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Message info footer */}
            <Box
                sx={{
                    p: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: `1px solid ${theme[themeStyle].grey}30`,
                }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: isCurrentUser ? 'rgba(255, 255, 255, 0.7)' : theme[themeStyle].grey,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    {formatMessageTime(message.createdAt)}
                </Typography>

                {/* Add message actions */}
                <MessageActions
                    message={message}
                    isCurrentUser={isCurrentUser}
                    onReactionClick={onReactionClick}
                    onDeleteMessage={onDeleteMessage}
                    onEditMessage={onEditMessage}
                />
            </Box>
        </Box>
    );
};
